import { Autocomplete, Chip, TextField, Tooltip } from '@mui/material';
import React from 'react';

import { ToolName } from '../../Types/enums';
import { CreatePrompt } from '../../Types/prompt';
import { deepResearchToolsTooltipMessage } from '../../util/messageUtils';
import { handleToolDelete, ToolConfig } from '../../util/tools';

interface ToolOption {
  value: ToolName;
  label: string;
}

interface PromptToolsSelectorProps {
  options: ToolOption[];
  values: CreatePrompt;
  readOnly?: boolean;
  disabled?: boolean;
  onChange: (tools: ToolName[]) => void;
  onDelete: (tools: ToolName[]) => void;
}

const PromptToolsSelector = ({
  readOnly,
  disabled,
  options,
  values,
  onChange,
  onDelete,
}: PromptToolsSelectorProps) => (
  <Autocomplete
    multiple
    readOnly={readOnly}
    disabled={disabled}
    id="tools-standard"
    options={options}
    value={values.tools.map((tool) => ({ value: tool, label: ToolConfig[tool].name }))}
    onChange={(_, newValue) => onChange(newValue.map((tool) => tool.value))}
    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
      const inputValue = (e.target as HTMLInputElement).value.trim() as ToolName;
      if (
        e.key === 'Tab' &&
        inputValue &&
        !values.tools.includes(inputValue) &&
        Object.values(ToolName).includes(inputValue)
      ) {
        e.preventDefault();
        const newTools = [...values.tools, inputValue];
        onChange(newTools);
      }
    }}
    renderInput={(params) => (
      <TextField {...params} name="toolInput" variant="standard" label="Tools" />
    )}
    renderTags={(value, getTagProps, ownerState) =>
      value.map((option, index) => {
        const tagProps = getTagProps({ index });
        return (
          <Tooltip
            title={
              !ownerState.readOnly
                ? deepResearchToolsTooltipMessage(option.value, values.tools)
                : ''
            }
            placement="top"
            key={option.value}
          >
            <div>
              <Chip
                {...tagProps}
                label={option.label}
                onDelete={
                  !ownerState.readOnly
                    ? handleToolDelete(option.value, values.tools, onDelete)
                    : undefined
                }
                disabled={
                  tagProps.disabled ||
                  (values.tools.includes(ToolName.DEEP_RESEARCH) &&
                    !ToolConfig[option.value].deepResearchSupport)
                }
              />
            </div>
          </Tooltip>
        );
      })
    }
    getOptionDisabled={(option) =>
      values.tools.includes(option.value) ||
      (values.tools.includes(ToolName.DEEP_RESEARCH) &&
        !ToolConfig[option.value]?.deepResearchSupport)
    }
  />
);

export default PromptToolsSelector;
